import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader, WidgetCard } from '@/components/admin/AdminShell';

/**
 * マスタトップ（指示書 §10 の業務マスター画面一覧の先頭 / D-074）。
 * `/masters` は期マスタを直接開いていたため、他のマスタへ辿り着けなかった。
 */
/**
 * 各マスタが「どこに効くか」まで書く（D-086）。
 * 「登録したものがどの画面に出るのか分からない」という指摘への対応。
 * 説明用の一覧は docs/ADMIN-MAP.html にも同じ内容がある。
 */
const MASTERS = [
  {
    to: '/masters/cohorts',
    label: '会員・組織（期）',
    description: '第1期・第2期などの期を登録します。',
    usedIn: '会員の「期」の選択肢／会員CSVの「期コード」／イベントの対象者抽出の条件',
  },
  {
    to: '/masters/courses',
    label: '会員・組織（コース）',
    description: '受講コースを登録します。期と組み合わせて会員を束ねます。',
    usedIn: '会員の「コース」の選択肢／会員CSVの「コースコード」／イベントの対象者抽出の条件',
  },
  {
    to: '/masters/event-types',
    label: 'イベント種別',
    description: '月例会・分科会・EXゼミ・サクセス面談・1on1などの種別を登録します。',
    usedIn: 'イベント作成の「種別」の選択肢／通知プリセットの適用対象',
  },
  {
    to: '/masters/survey-templates',
    label: 'アンケート・課題テンプレート',
    description: '設問を作り、版として公開します。',
    usedIn:
      'イベント詳細「課題」タブの割当の選択肢（版を公開したものだけが出ます。作っただけでは配信されません）',
  },
  // 「社長アンケート設定」は D-146 で廃止。設問は固定6項目（As is / To be / 伸ばしてほしい
  // 領域・成長した点 / 満足度 / 課題）になり、実施回も入力時に書くだけになったため、
  // マスタとして設定するものが無くなった
  {
    to: '/masters/notification-presets',
    label: '通知プリセット',
    description: '「いつ・誰に・どの文面で送るか」のルールを設定します。',
    usedIn: 'イベントを公開したときに予約される通知（一覧は「通知管理」）',
  },
  {
    to: '/masters/notification-templates',
    label: '通知テンプレート',
    description: '通知の件名・本文（氏名やイベント名を差し込めます）を用意します。',
    usedIn: '通知プリセットから参照され、実際に送られる文面になります',
  },
  {
    to: '/masters/ng-rules',
    label: '席次NG',
    description: '同じ卓にしない組み合わせを登録します（サクセス担当からの申請の承認もここ）。',
    usedIn: 'イベント詳細「席次」タブ。違反する配置には警告が出ます',
  },
  {
    to: '/integrations',
    label: '外部連携（連携設定）',
    description: 'Google・Slack・Chatwork・メール送信の接続情報を登録します。',
    usedIn: '通知が実際に届くかどうか／Meet参加ログ・Drive資料の取り込み',
  },
];

export function MastersHomePage(): ReactElement {
  return (
    <>
      <PageHeader
        title="業務マスター"
        description="ここは「他の画面の選択肢を作る場所」です。ここに登録していないものは、イベント作成や会員登録のプルダウンに出てきません。運用ルールの変更は、なるべくここの編集だけで済むようにしています。"
      />
      <div className="grid gap-4 md:grid-cols-2">
        {MASTERS.map((item) => (
          <WidgetCard key={item.to} title={item.label}>
            <p className="mb-2 text-wptext-muted">{item.description}</p>
            <p className="mb-2 text-wptext-muted">
              <span className="font-semibold text-wptext-strong">ここでの登録が出てくる場所: </span>
              {item.usedIn}
            </p>
            <Link to={item.to} className="text-wpaccent hover:underline">
              開く →
            </Link>
          </WidgetCard>
        ))}
      </div>
    </>
  );
}
